import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconUser, 
  IconBuilding, 
  IconCheck
} from '@tabler/icons-react';
import styles from './ProfileChoicePage.module.css';
import { ROUTES } from '../constants/routes';
import { userService } from '../services/api';

/**
 * Page permettant au nouvel utilisateur de choisir son type de profil
 * lors de sa première connexion (Utilisateur standard ou Organisation)
 */
const ProfileChoicePage: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<'user' | 'organization' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleOptionClick = (option: 'user' | 'organization') => {
    setSelectedOption(option);
    setError(null);
  };

  const handleContinue = async () => {
    if (!selectedOption) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Enregistrer le choix de l'utilisateur
      await userService.setProfileType(selectedOption);
      
      // Rediriger vers la page appropriée
      if (selectedOption === 'user') {
        navigate(ROUTES.USER_PROFILE);
      } else if (selectedOption === 'organization') {
        navigate(ROUTES.ORGANIZATION_CREATE);
      }
    } catch (err) {
      console.error('Erreur lors de la définition du type de profil:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Bienvenue sur EtuDown</h1>
      <p className={styles.subtitle}>
        Veuillez choisir le type de compte que vous souhaitez créer
      </p>

      <div className={styles.optionsContainer}>
        {/* Option Utilisateur */}
        <div 
          className={`${styles.optionCard} ${selectedOption === 'user' ? styles.cardSelected : ''}`}
          onClick={() => handleOptionClick('user')}
        >
          <div className={`${styles.cardHeader} ${styles.userHeader}`}>
            <IconUser size={48} className={styles.cardIcon} />
            <h2 className={styles.cardTitle}>Étudiant</h2>
            <p className={styles.cardSubtitle}>Compte utilisateur standard</p>
          </div>
          <div className={styles.cardBody}>
            <ul className={styles.featuresList}>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Découvrez des événements locaux</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Enregistrez vos événements favoris</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Personnalisez vos centres d'intérêt</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Recevez des notifications pertinentes</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Créez un profil staff (DJ, Photographe, etc.)</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Option Organisation */}
        <div 
          className={`${styles.optionCard} ${selectedOption === 'organization' ? styles.cardSelected : ''}`}
          onClick={() => handleOptionClick('organization')}
        >
          <div className={`${styles.cardHeader} ${styles.organizationHeader}`}>
            <IconBuilding size={48} className={styles.cardIcon} />
            <h2 className={styles.cardTitle}>Organisation</h2>
            <p className={styles.cardSubtitle}>Compte pour associations et entreprises</p>
          </div>
          <div className={styles.cardBody}>
            <ul className={styles.featuresList}>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Créez et gérez des événements</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Publiez des offres pour trouver du staff</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Tableau de bord d'analyse</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Page d'organisation personnalisée</p>
              </li>
              <li className={styles.featureItem}>
                <IconCheck size={18} className={styles.featureIcon} />
                <p className={styles.featureText}>Gestion avancée des participants</p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className={styles.actionContainer}>
        {error && <p className={styles.error}>{error}</p>}
        <button 
          className={styles.continueButton}
          onClick={handleContinue}
          disabled={!selectedOption || isLoading}
        >
          {isLoading ? 'Chargement...' : 'Continuer'}
        </button>
        <div className={styles.loginLink}>
          Vous avez déjà un compte ? <a href="/dev/login">Se connecter</a>
        </div>
      </div>
    </div>
  );
};

export default ProfileChoicePage;
